/**
 * Calculate age from date of birth
 * @param {Date|string} dateOfBirth - Date of birth
 * @returns {number|null} Age in years, or null if dateOfBirth is invalid
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  
  // Check if date is valid
  if (isNaN(birthDate.getTime())) return null;
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // Adjust age if birthday hasn't occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Format age with years label
 * @param {Date|string} dateOfBirth - Date of birth
 * @param {Function} t - Translation function (optional)
 * @returns {string} Formatted age string
 */
export const formatAge = (dateOfBirth, t = null) => {
  const age = calculateAge(dateOfBirth);
  if (age === null) return t ? t('common.notAvailable', { defaultValue: 'N/A' }) : 'N/A';
  
  const ageText = t 
    ? t('profile.ageYears', { age, defaultValue: `${age} years` })
    : `${age} years`;
  
  return ageText;
};

/**
 * Get the next month change date (when age should update)
 * @returns {Date} Date when next month starts
 */
export const getNextMonthChange = () => {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return nextMonth;
};

/**
 * Calculate milliseconds until next month change
 * @returns {number} Milliseconds until next month
 */
export const getMsUntilNextMonth = () => {
  const nextMonth = getNextMonthChange();
  return nextMonth.getTime() - Date.now();
};

